import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useState } from "react";
import { X } from "lucide-react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import CanonicalTag from "@/components/CanonicalTag";
import digitalNomadImage from "@/assets/digital-nomad-adu.jpg";

const HowToUseADUDigitalNomadHQ = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <CanonicalTag url="https://www.udaadu.com/how-to-use-adu-digital-nomad-hq" />
      <Navigation />
        {/* Hero Section */}
        <section className="relative overflow-hidden py-20 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-8">
                <h1 className="text-4xl lg:text-6xl font-bold leading-tight" style={{ color: '#E4683B' }}>
                  Your Flexible Home Base
                </h1>
                <p className="text-xl text-foreground leading-relaxed">
                  A smart ADU HQ gives digital nomads flexibility, security, and professional space.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    size="lg" 
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={openCalendlyModal}
                  >
                    📅 Book & Schedule
                  </Button>
                </div>
              </div>
              <div className="relative">
                <img 
                  src={digitalNomadImage} 
                  alt="Modern digital nomad ADU with compact workspace and smart storage" 
                  className="rounded-lg shadow-2xl w-full h-auto"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Features for Remote Life Section */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-3xl font-bold mb-12 text-center" style={{ color: '#E4683B' }}>
              Features for Remote Life
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-4 text-foreground">Compact Workspace</h3>
                  <p className="text-muted-foreground">Efficient desk setup with high-speed Wi-Fi infrastructure for seamless remote work.</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-4 text-foreground">Smart Security</h3>
                  <p className="text-muted-foreground">Smart lock and keyless entry system for secure, convenient access between travels.</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-4 text-foreground">Flexible Living</h3>
                  <p className="text-muted-foreground">Convertible bed and compact kitchenette designed for short-term and extended stays.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Work-Life Flexibility */}
        <section className="py-16 px-4 bg-muted/10">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl font-bold mb-8" style={{ color: '#E4683B' }}>
              Work-Life Flexibility
            </h2>
            <div className="grid md:grid-cols-2 gap-8 text-left">
              <div>
                <h3 className="text-xl font-semibold mb-4 text-foreground">Airbnb Potential</h3>
                <p className="text-muted-foreground">Generate rental income when traveling by listing as short-term rental.</p>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-4 text-foreground">Zero Commute</h3>
                <p className="text-muted-foreground">Seamless transition between remote work periods and travel adventures.</p>
              </div>
            </div>
            <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-white text-[#E4683B] hover:bg-white/90"
                onClick={() => window.open('/landing', '_blank')}
              >
                📐 What fits my lot?
              </Button>
              <Button 
                size="lg"
                className="bg-[#E4683B] text-white hover:bg-[#E4683B]/90"
                onClick={() => window.open('https://udaadu.com/adu-calculator', '_blank')}
              >
                💰 Get Price & ROI Report
              </Button>
            </div>
          </div>
        </section>

        {/* ROI & Resale Section */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-3xl font-bold mb-12 text-center" style={{ color: '#E4683B' }}>
              ROI & Resale Potential
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="text-center">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Multi-Purpose Investment</h3>
                <p className="text-muted-foreground">Versatile space serves as office, guest suite, or rental unit to maximize investment returns.</p>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Remote Worker Appeal</h3>
                <p className="text-muted-foreground">High market demand from remote workers and digital professionals seeking flexible living solutions.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-16 px-4 bg-primary/10">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl font-bold mb-8 text-foreground">
              Build a business that grows from home—without being in your home.
            </h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-[#E4683B] text-white hover:bg-[#E4683B]/90"
                onClick={() => window.open('/our-work', '_blank')}
              >
                🏡 Our Portfolio
              </Button>
              <Button 
                size="lg"
                className="bg-[#E4683B] text-white hover:bg-[#E4683B]/90"
                onClick={() => window.open('https://udaadu.com/faq', '_blank')}
              >
                ❓ Our FAQ
              </Button>
            </div>
          </div>
        </section>

        {/* Calendly Modal */}
        {showCalendly && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="relative w-full max-w-2xl h-[70vh] bg-white rounded-lg overflow-hidden shadow-xl">
              <button 
                onClick={closeCalendlyModal} 
                className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
              >
                <X className="h-6 w-6 text-gray-600" />
              </button>
              <iframe 
                src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" 
                width="100%" 
                height="100%" 
                frameBorder="0" 
                title="Schedule a meeting" 
              />
            </div>
          </div>
        )}
        
        <Footer />
        <FloatingADUWizard />
      </div>
  );
};

export default HowToUseADUDigitalNomadHQ;